import {
  CaptionStyleDto,
  CaptionTextStyleDto,
  CaptionActiveTextStyleDto,
  CaptionBackgroundStyleDto,
  CaptionDto,
  CaptionDisplayMode,
  CaptionShadowDto,
  CaptionAnimationDto,
  CaptionTextTransformDto,
} from "@repo/api/caption";
import { CompositionCaptionDto } from "@repo/api/editor";
import { FontDto, FontStyleDto } from "@repo/api/font";
import {
  IconChevronLeft,
  IconLayersSelected,
  IconTrash,
} from "@tabler/icons-react";
import { useCallback, useState } from "react";
import { Button } from "@/lib/components/Button/Button";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { PercentageInput } from "@/lib/components/PercentageInput";
import { ColorPicker } from "@/lib/components/ColorPicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/lib/components/Select";
import SavePresetDialog from "./SavePresetDialog";
import DeletePresetDialog from "./DeletePresetDialog";
import { ShadowPicker } from "@/lib/components/ShadowPicker";
import { FontSelect } from "@/features/Editor/OverlaysTab/ElementEditor/FontSelect";
import { AnimationSelect } from "./AnimationSelect";
import {
  TEXT_TRANSFORM_OPTIONS,
  TextTransformSelect,
} from "./TextTransformSelect";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import { DEFAULT_ASPECT_RATIOS_POSITION } from "@repo/video-engine-i2v/components/Captions/CaptionPresets";

const DISPLAY_MODE_OPTIONS: { label: string; value: CaptionDisplayMode }[] = [
  { label: "Static", value: "STATIC" },
  { label: "Highlighted", value: "HIGHLIGHTED" },
  { label: "Single Word", value: "SINGLE_WORD" },
];

const getCurrentDisplayModeLabel = (displayMode: CaptionDisplayMode) => {
  const currentMode = DISPLAY_MODE_OPTIONS.find(
    (option) => option.value === displayMode,
  );
  return currentMode ? currentMode.label : "Unknown";
};

type CaptionSettingsProps = {
  onBack: () => void;
  selectedPreset: CaptionDto;
  customCaptions: CaptionDto[];
  compositionCaption: CompositionCaptionDto;
};

function CaptionSettings({
  onBack,
  selectedPreset,
  customCaptions,
  compositionCaption,
}: CaptionSettingsProps) {
  const teamExternalId = useEditorStore((state) => state.teamExternalId);
  const updateVideo = useEditorStore((state) => state.service.updateVideo);
  const aspectRatio = useEditorStore(
    (state) => state.video!.compositionJson.aspectRatio,
  );
  const style = compositionCaption?.config.style;
  const displayMode: CaptionDisplayMode =
    compositionCaption?.config.displayMode ?? "STATIC";
  const animation = style?.animation?.type ?? "NONE";

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const isCustomPreset = customCaptions.some(
    (c) => c.externalId === selectedPreset.externalId,
  );

  const opacity = style?.opacity ?? 100;
  const textColor = style?.text.color ?? "#FFFFFF";
  const textOpacity = style?.text.opacity ?? 100;
  const yPosition =
    style?.position?.[aspectRatio]?.y ??
    DEFAULT_ASPECT_RATIOS_POSITION[aspectRatio]!.y;
  const fontSize = style?.text.fontSize ?? 50;
  const fontWeight = style?.text.fontWeight ?? 600;
  const fontStyle: FontStyleDto = style?.text.fontStyle ?? "normal";
  const textTransform: CaptionTextTransformDto | "NONE" =
    TEXT_TRANSFORM_OPTIONS.find(
      (option) => option === style?.text.textTransform,
    ) || "NONE";
  const backgroundColor = style?.background?.color ?? "#FFFFFF";
  const backgroundOpacity = style?.background?.opacity ?? 0;
  const activeTextColor = style?.activeText?.color ?? "#FFFFFF";
  const activeTextOpacity = style?.activeText?.opacity ?? 100;
  const textShadow = style?.text.textShadow ?? {
    color: "#000000",
    opacity: 0,
    x: 0,
    y: 2,
    blur: 4,
  };
  const backgroundShadow = style?.background?.boxShadow ?? {
    color: "#000000",
    opacity: 0,
    x: 0,
    y: 2,
    blur: 4,
  };

  const updateStyle = useCallback(
    (updates: Partial<CaptionStyleDto>) => {
      updateVideo((video) => {
        const currentCaptions = video.compositionJson.captions;
        if (
          currentCaptions &&
          "externalId" in currentCaptions &&
          "config" in currentCaptions
        ) {
          Object.assign(currentCaptions.config.style, updates);
        }
      });
    },
    [updateVideo],
  );

  const updateTextStyle = useCallback(
    (updates: Partial<CaptionTextStyleDto>) => {
      updateVideo((video) => {
        const currentCaptions = video.compositionJson.captions;
        if (
          currentCaptions &&
          "externalId" in currentCaptions &&
          "config" in currentCaptions
        ) {
          const style = currentCaptions.config.style;
          Object.assign(style.text, updates);
        }
      });
    },
    [updateVideo],
  );

  const updatePosition = useCallback(
    (y: number) => {
      updateVideo((video) => {
        const currentCaptions = video.compositionJson.captions;
        if (
          currentCaptions &&
          "externalId" in currentCaptions &&
          "config" in currentCaptions
        ) {
          if (!currentCaptions.config.style.position) {
            currentCaptions.config.style.position = {};
          }
          currentCaptions.config.style.position[aspectRatio] = { y };
        }
      });
    },
    [updateVideo, aspectRatio],
  );

  const updateBackgroundStyle = useCallback(
    (updates: Partial<CaptionBackgroundStyleDto>) => {
      updateVideo((video) => {
        const currentCaptions = video.compositionJson.captions;
        if (
          currentCaptions &&
          "externalId" in currentCaptions &&
          "config" in currentCaptions
        ) {
          const style = currentCaptions.config.style;
          if (!style.background) style.background = {};
          Object.assign(style.background, updates);
        }
      });
    },
    [updateVideo],
  );

  const updateActiveTextStyle = useCallback(
    (updates: Partial<CaptionActiveTextStyleDto>) => {
      updateVideo((video) => {
        const currentCaptions = video.compositionJson.captions;
        if (
          currentCaptions &&
          "externalId" in currentCaptions &&
          "config" in currentCaptions
        ) {
          const style = currentCaptions.config.style;
          if (!style.activeText) style.activeText = {};
          Object.assign(style.activeText, updates);
        }
      });
    },
    [updateVideo],
  );

  const updateDisplayMode = useCallback(
    (mode: CaptionDisplayMode) => {
      updateVideo((video) => {
        const currentCaptions = video.compositionJson.captions;
        if (
          currentCaptions &&
          "externalId" in currentCaptions &&
          "config" in currentCaptions
        ) {
          currentCaptions.config.displayMode = mode;
        }
      });
    },
    [updateVideo],
  );

  const updateAnimation = useCallback(
    (value: "NONE" | CaptionAnimationDto["type"]) => {
      updateVideo((video) => {
        const currentCaptions = video.compositionJson.captions;
        if (
          currentCaptions &&
          "externalId" in currentCaptions &&
          "config" in currentCaptions
        ) {
          if (value === "NONE") {
            delete currentCaptions.config.style.animation;
          } else {
            currentCaptions.config.style.animation = {
              type: value,
            };
          }
        }
      });
    },
    [updateVideo],
  );

  const handleSelectFont = useCallback(
    ({
      font,
      fontWeight,
      fontStyle,
    }: {
      font: FontDto;
      fontWeight: number;
      fontStyle: FontStyleDto;
    }) => {
      updateTextStyle({
        fontExternalId: font.externalId,
        fontWeight,
        fontStyle,
      });
    },
    [updateTextStyle],
  );

  const handleFontWeightChange = useCallback(
    (newFontWeight: number) => {
      updateTextStyle({ fontWeight: newFontWeight });
    },
    [updateTextStyle],
  );

  const handleFontStyleChange = useCallback(
    (newFontStyle: FontStyleDto) => {
      updateTextStyle({ fontStyle: newFontStyle });
    },
    [updateTextStyle],
  );

  const handleFontSizeChange = useCallback(
    (newFontSize: number) => {
      updateTextStyle({ fontSize: newFontSize });
    },
    [updateTextStyle],
  );

  const handleTextTransformChange = useCallback(
    (value: CaptionTextTransformDto | "NONE") => {
      updateVideo((video) => {
        const currentCaptions = video.compositionJson.captions;
        if (
          currentCaptions &&
          "externalId" in currentCaptions &&
          "config" in currentCaptions
        ) {
          const style = currentCaptions.config.style;

          if (value === "NONE") {
            delete style.text.textTransform;
            return;
          }

          style.text.textTransform = value;
        }
      });
    },
    [updateVideo],
  );

  const updateTextShadow = useCallback(
    (updates: Partial<CaptionShadowDto>) => {
      updateVideo((video) => {
        const currentCaptions = video.compositionJson.captions;
        if (
          currentCaptions &&
          "externalId" in currentCaptions &&
          "config" in currentCaptions
        ) {
          const style = currentCaptions.config.style;
          if (!style.text.textShadow) {
            style.text.textShadow = {
              color: "#000000",
              opacity: 0,
              x: 0,
              y: 2,
              blur: 4,
            };
          }
          Object.assign(style.text.textShadow, updates);
        }
      });
    },
    [updateVideo],
  );

  const updateBackgroundShadow = useCallback(
    (updates: Partial<CaptionShadowDto>) => {
      updateVideo((video) => {
        const currentCaptions = video.compositionJson.captions;
        if (
          currentCaptions &&
          "externalId" in currentCaptions &&
          "config" in currentCaptions
        ) {
          const style = currentCaptions.config.style;
          if (!style.background) style.background = {};
          if (!style.background.boxShadow) {
            style.background.boxShadow = {
              color: "#000000",
              opacity: 0,
              x: 0,
              y: 2,
              blur: 4,
            };
          }
          Object.assign(style.background.boxShadow, updates);
        }
      });
    },
    [updateVideo],
  );

  return (
    <div className="flex h-full flex-col overflow-y-auto pb-8">
      <div className="mb-3 -ml-1 flex items-center justify-between px-6 py-5">
        <button
          className="flex cursor-pointer flex-row items-center gap-2 text-sm font-medium text-text-primary"
          onClick={onBack}
        >
          <IconChevronLeft
            className="text-text-primary"
            width={16}
            height={16}
          />
          {selectedPreset.name}
        </button>
        <div className="flex items-center gap-3">
          <Button size="xs" onClick={() => setSaveDialogOpen(true)}>
            Save Preset
          </Button>
          {isCustomPreset && (
            <Button
              size="iconXs"
              variant="outline"
              onClick={() => setDeleteDialogOpen(true)}
            >
              <IconTrash size={18} />
            </Button>
          )}
        </div>
      </div>
      <div className="flex-1 space-y-8 px-6">
        <div>
          <div className="input-label">Display Mode</div>
          <Select
            value={displayMode}
            onValueChange={(value) =>
              updateDisplayMode(value as CaptionDisplayMode)
            }
          >
            <SelectTrigger className="h-9 w-50">
              <SelectValue>
                {getCurrentDisplayModeLabel(displayMode)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {DISPLAY_MODE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-row items-center gap-4">
          <div>
            <div className="input-label">Appearance</div>
            <PercentageInput
              className="w-50"
              value={opacity}
              onChange={(value) => updateStyle({ opacity: value })}
              leadingComponent={
                <IconLayersSelected
                  className="text-text-secondary"
                  stroke={1.5}
                  size={18}
                />
              }
            />
          </div>
          <div>
            <div className="input-label">
              Position{" "}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-default">
                    (
                    <span className="font-medium underline decoration-dashed decoration-0 underline-offset-3">
                      {aspectRatio}
                    </span>
                    )
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Each video aspect ratio has its own caption position. You're
                    currently editing {aspectRatio}.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            <PercentageInput
              className="w-40"
              value={yPosition}
              onChange={updatePosition}
              leadingComponent={
                <div className="text-sm text-text-secondary">Y</div>
              }
            />
          </div>
        </div>
        <div className="flex flex-row items-center gap-4">
          <div className="flex-1">
            <div className="input-label">Text Color</div>
            <ColorPicker
              value={textColor}
              opacity={textOpacity}
              onChange={(color) => updateTextStyle({ color })}
              onOpacityChange={(opacity) => updateTextStyle({ opacity })}
            />
          </div>
          <div className="flex-1">
            <div className="input-label">Text Shadow</div>
            <ShadowPicker
              value={{
                color: textShadow.color,
                opacity: textShadow.opacity,
                x: textShadow.x,
                y: textShadow.y,
                blur: textShadow.blur,
              }}
              onChange={updateTextShadow}
            />
          </div>
          {displayMode === "HIGHLIGHTED" && (
            <div className="flex-1">
              <div className="input-label">Active Text Color</div>
              <ColorPicker
                value={activeTextColor}
                opacity={activeTextOpacity}
                onChange={(color) => updateActiveTextStyle({ color })}
                onOpacityChange={(opacity) =>
                  updateActiveTextStyle({ opacity })
                }
              />
            </div>
          )}
        </div>
        <div className="flex flex-row items-center gap-4">
          <div className="flex-1">
            <div className="input-label">Background Color</div>
            <ColorPicker
              value={backgroundColor}
              opacity={backgroundOpacity}
              onChange={(color) => updateBackgroundStyle({ color })}
              onOpacityChange={(opacity) => updateBackgroundStyle({ opacity })}
            />
          </div>
          <div className="flex-1">
            <div className="input-label">Background Shadow</div>
            <ShadowPicker
              value={{
                color: backgroundShadow.color,
                opacity: backgroundShadow.opacity,
                x: backgroundShadow.x,
                y: backgroundShadow.y,
                blur: backgroundShadow.blur,
              }}
              onChange={updateBackgroundShadow}
            />
          </div>
        </div>
        <div>
          <div className="input-label">Font</div>
          <FontSelect
            fontExternalId={style.text.fontExternalId}
            fontWeight={fontWeight}
            fontStyle={fontStyle}
            fontSize={fontSize}
            onSelectFont={handleSelectFont}
            onFontWeightChange={handleFontWeightChange}
            onFontStyleChange={handleFontStyleChange}
            onFontSizeChange={handleFontSizeChange}
            className="mb-4"
          />
        </div>
        <div className="flex flex-row items-center gap-4">
          <div>
            <div className="input-label">Text Transform</div>
            <TextTransformSelect
              value={textTransform}
              onValueChange={handleTextTransformChange}
            />
          </div>
          <div>
            <div className="input-label">Animation</div>
            <AnimationSelect
              displayMode={displayMode}
              animation={animation}
              onAnimationChange={updateAnimation}
            />
          </div>
        </div>
      </div>
      <SavePresetDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        teamExternalId={teamExternalId}
        selectedPreset={selectedPreset}
        compositionCaption={compositionCaption}
        customCaptions={customCaptions}
        isCustomPreset={isCustomPreset}
      />
      <DeletePresetDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        selectedPreset={selectedPreset}
        teamExternalId={teamExternalId}
        onDeleted={onBack}
      />
    </div>
  );
}

export default CaptionSettings;
